import type { ParserDefinitionSpec } from '../parser-layer';

export type ParserManifestCapability =
  | 'pdf:parse'
  | 'layout:tables'
  | 'layout:bbox'
  | 'citations:anchors'
  | `provider:${string}`;

export interface ParserManifestProjection {
  schemaVersion: string;
  protocolVersion: string;
  id: string;
  version: string;
  displayName: string;
  description?: string;
  mode: string;
  entrypoint: string;
  runtime: {
    mode: 'cloudflare-dynamic-worker';
    selfDeployable: boolean;
  };
  capabilities: ParserManifestCapability[];
  allowedHosts: string[];
  providerBindings: Array<{
    name: string;
    provider: string;
    defaultCredentialMode: 'self-deploy-byok';
  }>;
  requiredSecrets: Array<{
    name: string;
    provider: string;
    requiredFor: 'self-deploy';
  }>;
  credentialModes: ['self-deploy-byok'];
  ui?: { entry?: string; embed?: boolean; [key: string]: unknown };
  sourceUrl?: string;
  license?: string;
}

export interface ParserManifestProjectionOptions {
  readonly provider?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly mode?: string;
  readonly entrypoint?: string;
  readonly allowedHosts?: readonly string[];
  readonly requiredSecrets?: readonly string[];
  readonly ui?: {
    readonly entry?: string;
    readonly embed?: boolean;
    readonly [key: string]: unknown;
  };
  readonly sourceUrl?: string;
  readonly license?: string;
}

export interface ParserManifestDefinition {
  readonly spec: ParserDefinitionSpec;
}

function capabilitiesFromSpec(
  spec: ParserDefinitionSpec,
  provider: string,
): ParserManifestCapability[] {
  const capabilities = new Set<ParserManifestCapability>();
  if (spec.inputs.includes('pdf-bytes')) capabilities.add('pdf:parse');
  if (spec.provides.includes('tables')) capabilities.add('layout:tables');
  if (spec.provides.includes('bbox')) capabilities.add('layout:bbox');
  if (spec.provides.includes('layout') || spec.provides.includes('bbox')) {
    capabilities.add('citations:anchors');
  }
  capabilities.add(`provider:${provider}`);
  return [...capabilities];
}

/** Project one parser definition into the canonical checked-in `okra.json` shape. */
export function parserManifestFromDefinition(
  definition: ParserManifestDefinition,
  options: ParserManifestProjectionOptions = {},
): ParserManifestProjection {
  const provider = options.provider ?? definition.spec.id;
  const requiredSecrets = options.requiredSecrets ?? definition.spec.requiredEnvKeys;
  const allowedHosts =
    options.allowedHosts ??
    (definition.spec.network === 'none' ? [] : definition.spec.network.allowedHosts);

  return {
    schemaVersion: '1',
    protocolVersion: '1.0',
    id: definition.spec.id,
    version: definition.spec.version,
    displayName: options.displayName ?? definition.spec.name,
    ...(options.description === undefined ? {} : { description: options.description }),
    mode: options.mode ?? 'agentic',
    entrypoint: options.entrypoint ?? 'src/parser.ts',
    runtime: { mode: 'cloudflare-dynamic-worker', selfDeployable: true },
    capabilities: capabilitiesFromSpec(definition.spec, provider),
    allowedHosts: [...allowedHosts],
    providerBindings: [{ name: provider, provider, defaultCredentialMode: 'self-deploy-byok' }],
    requiredSecrets: requiredSecrets.map((name) => ({
      name,
      provider,
      requiredFor: 'self-deploy',
    })),
    credentialModes: ['self-deploy-byok'],
    ...(options.ui === undefined ? {} : { ui: { ...options.ui } }),
    ...(options.sourceUrl === undefined ? {} : { sourceUrl: options.sourceUrl }),
    ...(options.license === undefined ? {} : { license: options.license }),
  };
}
